import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { publicUrl } from '../../lib/publicUrl';
import {
  CreditCard, FileText, DollarSign, TrendingUp, Plus, Send,
  CheckCircle, Clock, AlertTriangle, Trash2, Eye, EyeOff, Mail, Banknote, Loader2,
  Copy, ExternalLink, ArrowRightLeft, Shield, Lock, KeyRound, Building2, Heart, Link2, Upload, Image, X
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PaymentSettings } from '@/components/PaymentSettings';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl relative overflow-hidden transition-all duration-300 ${className}`} {...props}>{children}</div>
);

const invStatusColors = {
  draft: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  sent: 'bg-blue-500/10 text-[#0075FF] border-blue-500/20',
  paid: 'bg-green-500/10 text-[#00CC99] border-green-500/20',
  overdue: 'bg-red-500/10 text-[#FF6B6B] border-red-500/20',
  partial: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20',
};

export default function DashboardFinances() {
  const [tab, setTab] = useState('overview');
  const [finances, setFinances] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [payMethod, setPayMethod] = useState('');
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '', client_email: '', due_date: '', notes: '',
    items: [{ description: '', quantity: 1, amount: 0 }]
  });

  // Pay Links state
  const [payLinks, setPayLinks] = useState([]);
  const [showCreatePayLink, setShowCreatePayLink] = useState(false);
  const [payLinkForm, setPayLinkForm] = useState({ item_name: '', amount: '', item_type: 'product', description: '', image_url: '' });
  const [payLinkUploading, setPayLinkUploading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/finances/overview`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/invoices`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/payment-settings`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/pay-links`, { headers }).catch(() => ({ data: [] })),
    ]).then(([fin, inv, pay, links]) => {
      setFinances(fin.data);
      setInvoices(inv.data || []);
      setPaymentSettings(pay.data || {});
      setPayLinks(links.data || []);
    });
  };
  useEffect(() => { fetchData(); }, []);

  // ---- Transfer Funds State ----
  const [pinStatus, setPinStatus] = useState({ has_pin: false });
  const [bankingInfo, setBankingInfo] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [transferBalance, setTransferBalance] = useState({ total_revenue: 0, total_transferred: 0, available_balance: 0 });
  const [showSetPin, setShowSetPin] = useState(false);
  const [pinForm, setPinForm] = useState({ pin: '', confirm_pin: '', password: '' });
  const [showAddBank, setShowAddBank] = useState(false);
  const [bankMethod, setBankMethod] = useState('bank_account');
  const [bankForm, setBankForm] = useState({ account_holder: '', institution: '', account_number: '', routing_number: '', etransfer_email: '' });
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferStep, setTransferStep] = useState('amount'); // amount -> pin -> code -> confirm
  const [transferForm, setTransferForm] = useState({ amount: '', method: '' });
  const [verifyPin, setVerifyPin] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [transferToken, setTransferToken] = useState('');
  const [mockCode, setMockCode] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const fetchTransferData = () => {
    Promise.all([
      axios.get(`${API}/security/pin/status`, { headers }).catch(() => ({ data: { has_pin: false } })),
      axios.get(`${API}/banking/info`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/transfers`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/transfers/balance`, { headers }).catch(() => ({ data: { total_revenue: 0, total_transferred: 0, available_balance: 0 } })),
    ]).then(([pin, bank, trans, bal]) => {
      setPinStatus(pin.data);
      setBankingInfo(bank.data || []);
      setTransfers(trans.data || []);
      setTransferBalance(bal.data);
    });
  };

  useEffect(() => { if (tab === 'transfers') fetchTransferData(); }, [tab]);

  const handleSetPin = async () => {
    if (pinForm.pin !== pinForm.confirm_pin) return toast.error('PINs do not match');
    if (pinForm.pin.length < 4 || pinForm.pin.length > 6) return toast.error('PIN must be 4-6 digits');
    if (!pinForm.password) return toast.error('Current password required');
    try {
      await axios.post(`${API}/security/pin`, { pin: pinForm.pin, password: pinForm.password }, { headers });
      toast.success('Security PIN set!');
      setShowSetPin(false);
      setPinForm({ pin: '', confirm_pin: '', password: '' });
      fetchTransferData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set PIN'); }
  };

  const handleSaveBankInfo = async () => {
    try {
      const payload = bankMethod === 'bank_account'
        ? { method: 'bank_account', ...bankForm }
        : { method: 'etransfer', etransfer_email: bankForm.etransfer_email };
      await axios.post(`${API}/banking/info`, payload, { headers });
      toast.success(`${bankMethod === 'etransfer' ? 'E-Transfer' : 'Bank account'} info saved securely`);
      setShowAddBank(false);
      setBankForm({ account_holder: '', institution: '', account_number: '', routing_number: '', etransfer_email: '' });
      fetchTransferData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeleteBankInfo = async (method) => {
    if (!(await themedConfirm('Remove this banking info?'))) return;
    try {
      await axios.delete(`${API}/banking/info/${method}`, { headers });
      toast.success('Banking info removed');
      fetchTransferData();
    } catch { toast.error('Failed to remove'); }
  };

  const handleSendVerificationCode = async () => {
    setTransferLoading(true);
    try {
      const res = await axios.post(`${API}/security/send-code`, { pin: verifyPin }, { headers });
      toast.success(res.data.message);
      if (res.data.mock_code) setMockCode(res.data.mock_code);
      setTransferStep('code');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send code'); }
    finally { setTransferLoading(false); }
  };

  const handleVerifyCode = async () => {
    setTransferLoading(true);
    try {
      const res = await axios.post(`${API}/security/verify-code`, { code: verifyCode }, { headers });
      setTransferToken(res.data.transfer_token);
      setTransferStep('confirm');
      toast.success('Verified!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    finally { setTransferLoading(false); }
  };

  const handleConfirmTransfer = async () => {
    setTransferLoading(true);
    try {
      const res = await axios.post(`${API}/transfers/request`, {
        transfer_token: transferToken,
        amount: parseFloat(transferForm.amount),
        method: transferForm.method,
      }, { headers });
      toast.success(res.data.message);
      setShowTransfer(false);
      resetTransferFlow();
      fetchTransferData();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Transfer failed'); }
    finally { setTransferLoading(false); }
  };

  const resetTransferFlow = () => {
    setTransferStep('amount');
    setTransferForm({ amount: '', method: '' });
    setVerifyPin('');
    setVerifyCode('');
    setTransferToken('');
    setMockCode('');
  };

  const startTransfer = () => {
    if (!pinStatus.has_pin) { toast.error('Set up your security PIN first'); setShowSetPin(true); return; }
    if (bankingInfo.length === 0) { toast.error('Add banking info first'); setShowAddBank(true); return; }
    resetTransferFlow();
    setShowTransfer(true);
  };

  const methodLabels = { bank_account: 'Bank Account', etransfer: 'E-Transfer', stripe: 'Stripe', paypal: 'PayPal' };
  const methodColors = { bank_account: '#3B82F6', etransfer: '#00CC99', stripe: '#635BFF', paypal: '#0070BA' };

  // ---- Donations State ----
  const [donations, setDonations] = useState([]);
  const [donationStats, setDonationStats] = useState({ total_raised: 0, donation_count: 0 });

  const fetchDonations = () => {
    Promise.all([
      axios.get(`${API}/donations`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/donations/stats`, { headers }).catch(() => ({ data: { total_raised: 0, donation_count: 0 } })),
    ]).then(([dons, stats]) => {
      setDonations(dons.data || []);
      setDonationStats(stats.data);
    });
  };

  useEffect(() => { if (tab === 'donations') fetchDonations(); }, [tab]);

  const addItem = () => setInvoiceForm(p => ({ ...p, items: [...p.items, { description: '', quantity: 1, amount: 0 }] }));
  const removeItem = (i) => setInvoiceForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setInvoiceForm(p => {
    const items = [...p.items];
    items[i] = { ...items[i], [field]: val };
    return { ...p, items };
  });

  const createInvoice = async () => {
    if (!invoiceForm.client_name || !invoiceForm.client_email) return toast.error('Client name and email required');
    try {
      await axios.post(`${API}/invoices`, invoiceForm, { headers });
      toast.success('Invoice created');
      setShowCreateInvoice(false);
      setInvoiceForm({ client_name: '', client_email: '', due_date: '', notes: '', items: [{ description: '', quantity: 1, amount: 0 }] });
      fetchData();
    } catch { toast.error('Failed to create invoice'); }
  };

  const updateInvoiceStatus = async (id, status, extra = {}) => {
    const payLink = publicUrl(`/invoice/pay/${id}`);
    await axios.put(`${API}/invoices/${id}`, { status, pay_link: payLink, ...extra }, { headers });
    toast.success(status === 'sent' ? 'Invoice sent! Email notification sent to customer.' : `Invoice marked as ${status}`);
    fetchData();
    if (selectedInvoice?.id === id) setSelectedInvoice(prev => prev ? { ...prev, status, ...extra } : prev);
  };

  const deleteInvoice = async (id) => {
    if (!(await themedConfirm('Delete this invoice?'))) return;
    await axios.delete(`${API}/invoices/${id}`, { headers });
    toast.success('Invoice deleted');
    fetchData();
  };

  // Pay Link functions
  const handlePayLinkImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPayLinkUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload?category=pay-links`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setPayLinkForm(p => ({ ...p, image_url: process.env.REACT_APP_BACKEND_URL + res.data.url }));
    } catch { toast.error('Upload failed'); }
    finally { setPayLinkUploading(false); }
  };

  const createPayLink = async () => {
    if (!payLinkForm.item_name || !payLinkForm.amount) return toast.error('Item name and amount required');
    try {
      await axios.post(`${API}/pay-links`, { ...payLinkForm, amount: parseFloat(payLinkForm.amount) }, { headers });
      toast.success('Pay link created!');
      setShowCreatePayLink(false);
      setPayLinkForm({ item_name: '', amount: '', item_type: 'product', description: '', image_url: '' });
      fetchData();
    } catch { toast.error('Failed to create pay link'); }
  };

  const deletePayLink = async (id) => {
    if (!(await themedConfirm('Delete this pay link?'))) return;
    try {
      await axios.delete(`${API}/pay-links/${id}`, { headers });
      toast.success('Pay link deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePayLink = async (link) => {
    try {
      await axios.put(`${API}/pay-links/${link.id}`, { is_active: !link.is_active }, { headers });
      toast.success(link.is_active ? 'Pay link deactivated' : 'Pay link activated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setPayMethod('');
    setShowPayDialog(true);
  };

  const handleSendFunds = async () => {
    if (!selectedInvoice) return;
    if (payMethod === 'etransfer') {
      if (!paymentSettings.etransfer_email || !paymentSettings.etransfer_verified) {
        toast.error('E-Transfer email not verified. Set it up in Settings first.');
        return;
      }
      await updateInvoiceStatus(selectedInvoice.id, 'paid', { payment_method: 'etransfer' });
      toast.success(`E-Transfer funds sent to ${paymentSettings.etransfer_email}`);
    } else if (payMethod === 'paypal') {
      if (!paymentSettings.paypal_email || !paymentSettings.paypal_verified) {
        toast.error('PayPal email not verified. Set it up in Settings first.');
        return;
      }
      await updateInvoiceStatus(selectedInvoice.id, 'paid', { payment_method: 'paypal' });
      toast.success(`PayPal payment sent to ${paymentSettings.paypal_email}`);
    } else if (payMethod === 'stripe') {
      await updateInvoiceStatus(selectedInvoice.id, 'paid', { payment_method: 'stripe' });
      toast.success('Stripe payment recorded');
    }
    setShowPayDialog(false);
    setSelectedInvoice(null);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'pay-links', label: 'Pay Links', icon: Link2 },
    { id: 'donations', label: 'Donations', icon: Heart },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'payouts', label: 'Payout Settings', icon: Banknote },
  ];

  const invoiceTotal = invoiceForm.items.reduce((s, item) => s + (item.amount * item.quantity), 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  return (
    <div className="space-y-6" data-testid="finances-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Finances</h1>
          <p className="text-sm text-slate-400 mt-1">Billing, invoicing & cashflow</p>
        </div>
        {tab === 'invoices' && (
          <button onClick={() => setShowCreateInvoice(true)} className="bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl px-5 py-2.5 text-sm font-medium shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all flex items-center gap-2" data-testid="create-invoice-btn">
            <Plus size={16} /> New Invoice
          </button>
        )}
        {tab === 'pay-links' && (
          <button onClick={() => setShowCreatePayLink(true)} className="bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl px-5 py-2.5 text-sm font-medium shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all flex items-center gap-2" data-testid="create-pay-link-btn">
            <Plus size={16} /> New Pay Link
          </button>
        )}
      </div>

      <div className="flex gap-2" data-testid="finance-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-gradient-to-r from-[#8B5CF6]/20 to-[#3B82F6]/10 text-white border border-[#8B5CF6]/30' : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'}`} data-testid={`tab-${t.id}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Current Plan card */}
          <PlanCard />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="p-5" data-testid="fin-total-revenue">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]"><DollarSign size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Revenue</p>
              </div>
              <p className="text-2xl font-bold text-white">${(finances?.total_revenue || 0).toFixed(2)}</p>
            </GlassCard>
            <GlassCard className="p-5" data-testid="fin-ecommerce">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#00CC99] to-emerald-600"><TrendingUp size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Ecommerce</p>
              </div>
              <p className="text-2xl font-bold text-white">${(finances?.ecommerce_revenue || 0).toFixed(2)}</p>
            </GlassCard>
            <GlassCard className="p-5" data-testid="fin-invoice-rev">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500"><FileText size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Invoice Revenue</p>
              </div>
              <p className="text-2xl font-bold text-white">${(finances?.invoice_revenue || 0).toFixed(2)}</p>
            </GlassCard>
            <GlassCard className="p-5" data-testid="fin-pending">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500"><Clock size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Pending</p>
              </div>
              <p className="text-2xl font-bold text-yellow-400">${(finances?.pending_invoices || 0).toFixed(2)}</p>
            </GlassCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Revenue Breakdown</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-400">Ecommerce Sales</span><span className="text-white">${(finances?.ecommerce_revenue || 0).toFixed(2)}</span></div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full transition-all" style={{ width: `${Math.min(100, ((finances?.ecommerce_revenue || 0) / (finances?.total_revenue || 1)) * 100)}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-400">Invoice Revenue</span><span className="text-white">${(finances?.invoice_revenue || 0).toFixed(2)}</span></div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((finances?.invoice_revenue || 0) / (finances?.total_revenue || 1)) * 100)}%` }} /></div>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-xl text-center"><p className="text-2xl font-bold text-white">{finances?.total_orders || 0}</p><p className="text-xs text-slate-400 mt-1">Total Orders</p></div>
                <div className="p-4 bg-white/5 rounded-xl text-center"><p className="text-2xl font-bold text-white">{finances?.total_invoices || 0}</p><p className="text-xs text-slate-400 mt-1">Total Invoices</p></div>
                <div className="p-4 bg-white/5 rounded-xl text-center"><p className="text-2xl font-bold text-[#FF6B6B]">${(finances?.overdue_invoices || 0).toFixed(2)}</p><p className="text-xs text-slate-400 mt-1">Overdue</p></div>
                <div className="p-4 bg-white/5 rounded-xl text-center"><p className="text-2xl font-bold text-[#00CC99]">${(finances?.total_revenue || 0).toFixed(2)}</p><p className="text-xs text-slate-400 mt-1">Net Revenue</p></div>
              </div>
            </GlassCard>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {tab === 'invoices' && (
        <GlassCard>
          <table className="w-full text-left text-sm" data-testid="invoices-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                <th className="px-6 py-3 font-medium">Invoice #</th>
                <th className="px-6 py-3 font-medium">Client</th>
                <th className="px-6 py-3 font-medium">Amount</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Due Date</th>
                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => setSelectedInvoice(inv)} data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-6 py-4 text-white font-mono font-medium">{inv.invoice_number}</td>
                  <td className="px-6 py-4"><p className="text-white">{inv.client_name}</p><p className="text-xs text-slate-500">{inv.client_email}</p></td>
                  <td className="px-6 py-4 text-white font-semibold">${inv.total?.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${invStatusColors[inv.status] || invStatusColors.draft}`}>{inv.status}</span>
                    {inv.payment_method && <p className="text-[10px] text-[#64748B] mt-1 capitalize">via {inv.payment_method}</p>}
                  </td>
                  <td className="px-6 py-4 text-slate-400 text-xs">{formatDate(inv.due_date)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {inv.status === 'draft' && (
                        <button onClick={() => updateInvoiceStatus(inv.id, 'sent')} className="text-xs text-[#8B5CF6] hover:text-[#A78BFA] flex items-center gap-1" data-testid={`send-invoice-${inv.id}`}><Send size={12} /> Send</button>
                      )}
                      {(inv.status === 'sent' || inv.status === 'overdue') && (
                        <button onClick={() => handlePayInvoice(inv)} className="text-xs text-[#00CC99] hover:text-green-300 flex items-center gap-1" data-testid={`pay-invoice-${inv.id}`}><DollarSign size={12} /> Pay</button>
                      )}
                      {inv.status !== 'draft' && (
                        <button onClick={() => { navigator.clipboard.writeText(publicUrl(`/invoice/pay/${inv.id}`)); toast.success('Payment link copied!'); }} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1" data-testid={`copy-link-${inv.id}`}><Copy size={12} /> Link</button>
                      )}
                      <button onClick={() => setSelectedInvoice(inv)} className="text-slate-500 hover:text-white" data-testid={`view-invoice-${inv.id}`}><Eye size={14} /></button>
                      <button onClick={() => deleteInvoice(inv.id)} className="text-slate-500 hover:text-[#FF6B6B] transition-colors" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 opacity-30" /><p>No invoices yet. Create your first invoice!</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </GlassCard>
      )}


      {/* Pay Links Tab */}
      {tab === 'pay-links' && (
        <div className="space-y-6" data-testid="pay-links-tab">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]"><Link2 size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Active Links</p>
              </div>
              <p className="text-2xl font-bold text-white">{payLinks.filter(l => l.is_active).length}</p>
            </GlassCard>
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#00CC99] to-emerald-600"><DollarSign size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Collected</p>
              </div>
              <p className="text-2xl font-bold text-[#00CC99]">${payLinks.reduce((s, l) => s + (l.total_collected || 0), 0).toFixed(2)}</p>
            </GlassCard>
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500"><CreditCard size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Payments</p>
              </div>
              <p className="text-2xl font-bold text-white">{payLinks.reduce((s, l) => s + (l.payments_count || 0), 0)}</p>
            </GlassCard>
          </div>

          {/* Info */}
          <GlassCard className="p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-[#8B5CF6]/10 shrink-0"><Link2 size={16} className="text-[#A78BFA]" /></div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">How Pay Links Work</h3>
                <p className="text-xs text-slate-400">Create a shareable payment link with an item name, amount, and optional image. Share the link with your customer and they can pay directly via Stripe checkout.</p>
              </div>
            </div>
          </GlassCard>

          {/* Pay Links List */}
          {payLinks.length > 0 ? (
            <div className="space-y-3" data-testid="pay-links-list">
              {payLinks.map(link => (
                <GlassCard key={link.id} className={`p-4 flex items-center gap-4 ${!link.is_active ? 'opacity-50' : ''}`} data-testid={`pay-link-card-${link.id}`}>
                  {/* Image */}
                  <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-white/5">
                    {link.image_url ? (
                      <img src={link.image_url} alt={link.item_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Link2 size={18} className="text-[#64748B]" /></div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white truncate">{link.item_name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${link.item_type === 'service' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : link.item_type === 'subscription' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20'}`}>
                        {link.item_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <p className="text-sm text-[#00CC99] font-semibold">${link.amount?.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-500">{link.payments_count || 0} payments</p>
                      <p className="text-[10px] text-slate-500">${(link.total_collected || 0).toFixed(2)} collected</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { navigator.clipboard.writeText(publicUrl(`/pay/${link.id}`)); toast.success('Pay link copied!'); }} className="p-2 text-[#A78BFA] hover:text-white hover:bg-white/5 rounded-lg transition-colors" title="Copy link" data-testid={`copy-pay-link-${link.id}`}>
                      <Copy size={14} />
                    </button>
                    <button onClick={() => togglePayLink(link)} className={`p-2 rounded-lg transition-colors ${link.is_active ? 'text-[#00CC99] hover:bg-[#00CC99]/10' : 'text-[#64748B] hover:bg-white/5'}`} title={link.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-pay-link-${link.id}`}>
                      {link.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button onClick={() => deletePayLink(link.id)} className="p-2 text-[#64748B] hover:text-[#FF6B6B] hover:bg-red-500/5 rounded-lg transition-colors" data-testid={`delete-pay-link-${link.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </GlassCard>
              ))}
            </div>
          ) : (
            <GlassCard className="p-12 text-center">
              <Link2 size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="font-semibold text-white mb-2">No pay links yet</h3>
              <p className="text-slate-400 text-sm mb-4">Create your first shareable payment link.</p>
              <button onClick={() => setShowCreatePayLink(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl text-sm font-medium shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all">
                <Plus size={14} /> Create Pay Link
              </button>
            </GlassCard>
          )}
        </div>
      )}

      {/* Donations Tab */}
      {tab === 'donations' && (
        <div className="space-y-6" data-testid="donations-tab">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <GlassCard className="p-5" data-testid="donation-total">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 shadow-[0_0_12px_rgba(236,72,153,0.3)]"><Heart size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Raised</p>
              </div>
              <p className="text-2xl font-bold text-white">${donationStats.total_raised.toFixed(2)}</p>
            </GlassCard>
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]"><DollarSign size={16} className="text-white" /></div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Donations Received</p>
              </div>
              <p className="text-2xl font-bold text-white">{donationStats.donation_count}</p>
            </GlassCard>
          </div>

          {/* How to accept donations info */}
          <GlassCard className="p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-pink-500/10 shrink-0"><Heart size={16} className="text-pink-400" /></div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">Accepting Donations</h3>
                <p className="text-xs text-slate-400">Add a <span className="text-[#A78BFA] font-medium">Donations block</span> to your site via the Content Editor. Donors can contribute via credit card or PayPal directly on your storefront.</p>
              </div>
            </div>
          </GlassCard>

          {/* Donation History */}
          {donations.length > 0 ? (
            <GlassCard data-testid="donation-history">
              <div className="px-6 py-4 border-b border-white/5"><h3 className="text-base font-semibold text-white">Donation History</h3></div>
              <table className="w-full text-left text-sm">
                <thead><tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-6 py-3 font-medium">Date</th><th className="px-6 py-3 font-medium">Donor</th><th className="px-6 py-3 font-medium">Amount</th><th className="px-6 py-3 font-medium">Method</th><th className="px-6 py-3 font-medium">Message</th><th className="px-6 py-3 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {donations.map(d => (
                    <tr key={d.id} className="border-b border-white/5">
                      <td className="px-6 py-3.5 text-slate-300 text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-3.5"><p className="text-white text-sm">{d.donor_name || 'Anonymous'}</p>{d.donor_email && <p className="text-[10px] text-slate-500">{d.donor_email}</p>}</td>
                      <td className="px-6 py-3.5 text-white font-semibold">${d.amount.toFixed(2)}</td>
                      <td className="px-6 py-3.5"><span className="text-xs px-2 py-0.5 rounded-full border" style={{ color: d.method === 'stripe' ? '#635BFF' : '#0070BA', borderColor: d.method === 'stripe' ? '#635BFF30' : '#0070BA30', background: d.method === 'stripe' ? '#635BFF10' : '#0070BA10' }}>{d.method === 'stripe' ? 'Card' : 'PayPal'}</span></td>
                      <td className="px-6 py-3.5 text-slate-400 text-xs max-w-[200px] truncate">{d.message || '—'}</td>
                      <td className="px-6 py-3.5"><span className={`text-xs font-medium ${d.status === 'completed' ? 'text-[#00CC99]' : d.status === 'pending' ? 'text-amber-400' : 'text-slate-500'}`}>{d.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlassCard>
          ) : (
            <GlassCard className="p-12 text-center">
              <Heart size={40} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400 text-sm">No donations yet. Add a Donations block to your site to start accepting.</p>
            </GlassCard>
          )}
        </div>
      )}




      {/* Billing Tab */}
      {tab === 'billing' && (
        <div className="space-y-6">
          <GlassCard className="p-6" data-testid="billing-status">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)]"><CreditCard size={24} className="text-white" /></div>
              <div><h3 className="text-lg font-semibold text-white">Your Subscription</h3><p className="text-sm text-slate-400">Monthly hosting plan</p></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white/5 rounded-xl"><p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Plan</p><p className="text-white font-semibold">$30/month</p></div>
              <div className="p-4 bg-white/5 rounded-xl"><p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Status</p><p className="text-[#00CC99] font-semibold">Active</p></div>
              <div className="p-4 bg-white/5 rounded-xl"><p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Next Billing</p><p className="text-white font-semibold">{new Date(Date.now() + 30 * 86400000).toLocaleDateString()}</p></div>
              <div className="p-4 bg-white/5 rounded-xl"><p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Payment</p><p className="text-white font-semibold">Credit Card</p></div>
            </div>
          </GlassCard>
          {/* Payment Methods Summary */}
          <GlassCard className="p-6" data-testid="payment-methods-summary">
            <h3 className="text-lg font-semibold text-white mb-4">Configured Payment Methods</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-[#00CC99]/20"><Mail size={14} className="text-[#00CC99]" /></div>
                  <div><p className="text-sm text-white">Interac E-Transfer</p><p className="text-[10px] text-[#64748B]">{paymentSettings.etransfer_email || 'Not configured'}</p></div>
                </div>
                {paymentSettings.etransfer_verified ? <span className="text-xs text-[#00CC99] flex items-center gap-1"><CheckCircle size={12} /> Verified</span> : <span className="text-xs text-yellow-400 flex items-center gap-1"><AlertTriangle size={12} /> {paymentSettings.etransfer_email ? 'Unverified' : 'Not set'}</span>}
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-[#0070BA]/20"><CreditCard size={14} className="text-[#0070BA]" /></div>
                  <div><p className="text-sm text-white">PayPal</p><p className="text-[10px] text-[#64748B]">{paymentSettings.paypal_email || 'Not configured'}</p></div>
                </div>
                {paymentSettings.paypal_verified ? <span className="text-xs text-[#00CC99] flex items-center gap-1"><CheckCircle size={12} /> Verified</span> : <span className="text-xs text-yellow-400 flex items-center gap-1"><AlertTriangle size={12} /> {paymentSettings.paypal_email ? 'Unverified' : 'Not set'}</span>}
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-[#635BFF]/20"><CreditCard size={14} className="text-[#635BFF]" /></div>
                  <div><p className="text-sm text-white">Stripe</p><p className="text-[10px] text-[#64748B]">Credit card payments</p></div>
                </div>
                <span className="text-xs text-[#00CC99] flex items-center gap-1"><CheckCircle size={12} /> Available</span>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Payout Settings Tab */}
      {tab === 'payouts' && (
        <PaymentSettings />
      )}

      {/* Invoice Detail Dialog */}
      <Dialog open={!!selectedInvoice && !showPayDialog} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">Invoice {selectedInvoice?.invoice_number}</DialogTitle></DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4" data-testid="invoice-detail">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-[#64748B] text-xs">Client</p><p className="text-white">{selectedInvoice.client_name}</p><p className="text-xs text-[#94A3B8]">{selectedInvoice.client_email}</p></div>
                <div><p className="text-[#64748B] text-xs">Status</p><span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${invStatusColors[selectedInvoice.status] || invStatusColors.draft}`}>{selectedInvoice.status}</span></div>
                <div><p className="text-[#64748B] text-xs">Due Date</p><p className="text-white">{formatDate(selectedInvoice.due_date)}</p></div>
                <div><p className="text-[#64748B] text-xs">Created</p><p className="text-white">{formatDate(selectedInvoice.created_at)}</p></div>
              </div>
              {selectedInvoice.payment_method && (
                <div className="p-3 rounded-xl bg-[#00CC99]/10 border border-[#00CC99]/20">
                  <p className="text-xs text-[#00CC99] capitalize">Paid via {selectedInvoice.payment_method}</p>
                </div>
              )}
              <div className="border-t border-white/5 pt-3">
                <p className="text-sm font-medium text-white mb-2">Items</p>
                <div className="space-y-2">
                  {selectedInvoice.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-xl">
                      <div><p className="text-sm text-white">{item.description || 'Item'}</p><p className="text-xs text-[#64748B]">Qty: {item.quantity}</p></div>
                      <p className="text-sm text-white font-medium">${(item.amount * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-white/5">
                <span className="text-[#9CA3AF]">Total</span>
                <span className="text-xl font-bold text-white">${selectedInvoice.total?.toFixed(2)}</span>
              </div>
              {selectedInvoice.notes && <div className="text-xs text-[#64748B]"><p className="font-medium mb-1">Notes:</p><p>{selectedInvoice.notes}</p></div>}
              <div className="flex gap-2 pt-2 flex-wrap">
                {selectedInvoice.status === 'draft' && (
                  <button onClick={() => { updateInvoiceStatus(selectedInvoice.id, 'sent'); setSelectedInvoice(null); }} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/20 transition-colors flex items-center justify-center gap-1.5" data-testid="detail-send-btn"><Send size={14} /> Send Invoice</button>
                )}
                {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                  <button onClick={() => handlePayInvoice(selectedInvoice)} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all flex items-center justify-center gap-1.5" data-testid="detail-pay-btn"><DollarSign size={14} /> Pay Invoice</button>
                )}
                {selectedInvoice.status !== 'draft' && (
                  <>
                    <button onClick={() => { navigator.clipboard.writeText(publicUrl(`/invoice/pay/${selectedInvoice.id}`)); toast.success('Payment link copied!'); }} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-white/5 text-[#9CA3AF] border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5" data-testid="copy-payment-link-btn"><Copy size={14} /> Copy Payment Link</button>
                    <a href={`/invoice/pay/${selectedInvoice.id}`} target="_blank" rel="noopener noreferrer" className="py-2.5 px-4 rounded-xl text-sm font-medium bg-white/5 text-[#9CA3AF] border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5" data-testid="open-payment-page-btn"><ExternalLink size={14} /></a>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Invoice Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Banknote size={18} className="text-[#A78BFA]" /> Pay Invoice</DialogTitle></DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 mt-2">
              <div className="p-4 rounded-xl bg-white/5 border border-white/5 text-center">
                <p className="text-xs text-[#64748B] mb-1">{selectedInvoice.invoice_number}</p>
                <p className="text-2xl font-bold text-white">${selectedInvoice.total?.toFixed(2)}</p>
                <p className="text-xs text-[#9CA3AF] mt-1">to {selectedInvoice.client_name}</p>
              </div>

              <p className="text-sm text-[#9CA3AF]">Select payment method:</p>

              <div className="space-y-2">
                {/* E-Transfer */}
                <button onClick={() => setPayMethod('etransfer')} className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${payMethod === 'etransfer' ? 'bg-[#00CC99]/10 border-[#00CC99]/30 shadow-[0_0_15px_rgba(0,204,153,0.1)]' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="pay-method-etransfer">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#00CC99] to-emerald-600 shrink-0"><Mail size={14} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Interac E-Transfer</p>
                    <p className="text-[10px] text-[#64748B]">
                      {paymentSettings.etransfer_email && paymentSettings.etransfer_verified
                        ? `Send to ${paymentSettings.etransfer_email}`
                        : paymentSettings.etransfer_email ? 'Email not verified' : 'Not configured - set up in Settings'}
                    </p>
                  </div>
                  {paymentSettings.etransfer_verified && <CheckCircle size={14} className="text-[#00CC99] shrink-0" />}
                </button>

                {/* PayPal */}
                <button onClick={() => setPayMethod('paypal')} className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${payMethod === 'paypal' ? 'bg-[#0070BA]/10 border-[#0070BA]/30 shadow-[0_0_15px_rgba(0,112,186,0.1)]' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="pay-method-paypal">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#0070BA] to-[#003087] shrink-0"><CreditCard size={14} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">PayPal</p>
                    <p className="text-[10px] text-[#64748B]">
                      {paymentSettings.paypal_email && paymentSettings.paypal_verified
                        ? `Send to ${paymentSettings.paypal_email}`
                        : paymentSettings.paypal_email ? 'Email not verified' : 'Not configured - set up in Settings'}
                    </p>
                  </div>
                  {paymentSettings.paypal_verified && <CheckCircle size={14} className="text-[#00CC99] shrink-0" />}
                </button>

                {/* Stripe */}
                <button onClick={() => setPayMethod('stripe')} className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${payMethod === 'stripe' ? 'bg-[#635BFF]/10 border-[#635BFF]/30 shadow-[0_0_15px_rgba(99,91,255,0.1)]' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="pay-method-stripe">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] shrink-0"><CreditCard size={14} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Credit/Debit Card (Stripe)</p>
                    <p className="text-[10px] text-[#64748B]">Pay with card</p>
                  </div>
                  <CheckCircle size={14} className="text-[#00CC99] shrink-0" />
                </button>
              </div>

              <button onClick={handleSendFunds} disabled={!payMethod} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-40 transition-all text-sm flex items-center justify-center gap-2" data-testid="confirm-payment-btn">
                <DollarSign size={14} /> {payMethod === 'etransfer' ? 'Send via E-Transfer' : payMethod === 'paypal' ? 'Send via PayPal' : payMethod === 'stripe' ? 'Pay with Card' : 'Select a method'}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateInvoice} onOpenChange={setShowCreateInvoice}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">Create Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="create-invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Client Name</Label><Input value={invoiceForm.client_name} onChange={e => setInvoiceForm(p => ({...p, client_name: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="invoice-client-name" /></div>
              <div><Label className="text-slate-300">Client Email</Label><Input type="email" value={invoiceForm.client_email} onChange={e => setInvoiceForm(p => ({...p, client_email: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="invoice-client-email" /></div>
            </div>
            <div><Label className="text-slate-300">Due Date</Label><Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(p => ({...p, due_date: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" /></div>
            <div>
              <Label className="text-slate-300 mb-2 block">Line Items</Label>
              {invoiceForm.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center mb-2">
                  <Input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Description" className="bg-white/5 border-white/10 text-white flex-1 rounded-xl" />
                  <Input type="number" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseInt(e.target.value) || 1)} className="bg-white/5 border-white/10 text-white w-16 rounded-xl" />
                  <Input type="number" value={item.amount} onChange={e => updateItem(i, 'amount', parseFloat(e.target.value) || 0)} placeholder="$" className="bg-white/5 border-white/10 text-white w-24 rounded-xl" />
                  {invoiceForm.items.length > 1 && <button onClick={() => removeItem(i)} className="text-slate-500 hover:text-[#FF6B6B]"><Trash2 size={14} /></button>}
                </div>
              ))}
              <button onClick={addItem} className="text-xs text-[#A78BFA] hover:text-white mt-1">+ Add Item</button>
            </div>
            <div><Label className="text-slate-300">Notes</Label><Textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({...p, notes: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" rows={2} /></div>
            <div className="p-4 bg-white/5 rounded-xl flex items-center justify-between"><span className="text-slate-400 font-medium">Total</span><span className="text-xl font-bold text-white">${invoiceTotal.toFixed(2)}</span></div>
            <button onClick={createInvoice} className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl py-2.5 font-medium shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all" data-testid="submit-invoice-btn">Create Invoice</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set PIN Dialog */}
      <Dialog open={showSetPin} onOpenChange={setShowSetPin}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Shield size={18} className="text-amber-400" /> {pinStatus.has_pin ? 'Change' : 'Set Up'} Security PIN</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="set-pin-dialog">
            <p className="text-xs text-slate-400">Your PIN is required every time you initiate a fund transfer. Choose 4-6 digits.</p>
            <div><Label className="text-slate-300 text-xs">Current Password</Label><Input type="password" value={pinForm.password} onChange={e => setPinForm(p => ({...p, password: e.target.value}))} placeholder="Verify your identity" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="pin-password" /></div>
            <div><Label className="text-slate-300 text-xs">New PIN (4-6 digits)</Label><Input type="password" maxLength={6} value={pinForm.pin} onChange={e => setPinForm(p => ({...p, pin: e.target.value.replace(/\D/g, '')}))} placeholder="Enter PIN" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl tracking-[0.5em] text-center text-lg" data-testid="pin-input" /></div>
            <div><Label className="text-slate-300 text-xs">Confirm PIN</Label><Input type="password" maxLength={6} value={pinForm.confirm_pin} onChange={e => setPinForm(p => ({...p, confirm_pin: e.target.value.replace(/\D/g, '')}))} placeholder="Re-enter PIN" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl tracking-[0.5em] text-center text-lg" data-testid="pin-confirm" /></div>
            <button onClick={handleSetPin} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] transition-all text-sm" data-testid="save-pin-btn">Save PIN</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Banking Info Dialog */}
      <Dialog open={showAddBank} onOpenChange={setShowAddBank}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Building2 size={18} className="text-[#3B82F6]" /> Add Banking Info</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="add-bank-dialog">
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
              <Lock size={14} className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-300">Your banking info is encrypted end-to-end. The Master admin <strong>cannot</strong> view your account details.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setBankMethod('bank_account')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${bankMethod === 'bank_account' ? 'bg-[#3B82F6]/20 text-white border border-[#3B82F6]/30' : 'bg-white/5 text-slate-400 border border-transparent'}`} data-testid="bank-method-account">Bank Account</button>
              <button onClick={() => setBankMethod('etransfer')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${bankMethod === 'etransfer' ? 'bg-[#00CC99]/20 text-white border border-[#00CC99]/30' : 'bg-white/5 text-slate-400 border border-transparent'}`} data-testid="bank-method-etransfer">E-Transfer (Canada)</button>
            </div>
            {bankMethod === 'bank_account' ? (
              <div className="space-y-3">
                <div><Label className="text-slate-300 text-xs">Account Holder Name</Label><Input value={bankForm.account_holder} onChange={e => setBankForm(p => ({...p, account_holder: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="bank-holder" /></div>
                <div><Label className="text-slate-300 text-xs">Financial Institution</Label><Input value={bankForm.institution} onChange={e => setBankForm(p => ({...p, institution: e.target.value}))} placeholder="e.g., TD Bank, Chase" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="bank-institution" /></div>
                <div><Label className="text-slate-300 text-xs">Account Number</Label><Input type="password" value={bankForm.account_number} onChange={e => setBankForm(p => ({...p, account_number: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="bank-account-num" /></div>
                <div><Label className="text-slate-300 text-xs">Routing / Transit Number</Label><Input value={bankForm.routing_number} onChange={e => setBankForm(p => ({...p, routing_number: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="bank-routing" /></div>
              </div>
            ) : (
              <div><Label className="text-slate-300 text-xs">Interac E-Transfer Email</Label><Input type="email" value={bankForm.etransfer_email} onChange={e => setBankForm(p => ({...p, etransfer_email: e.target.value}))} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="bank-etransfer-email" /></div>
            )}
            <button onClick={handleSaveBankInfo} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all text-sm" data-testid="save-bank-btn">Save Securely</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Funds Flow Dialog */}
      <Dialog open={showTransfer} onOpenChange={(open) => { if (!open) { setShowTransfer(false); resetTransferFlow(); } }}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><ArrowRightLeft size={18} className="text-[#8B5CF6]" /> Transfer Funds</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="transfer-dialog">
            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 mb-2">
              {['amount', 'pin', 'code', 'confirm'].map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${['amount', 'pin', 'code', 'confirm'].indexOf(transferStep) >= i ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white' : 'bg-white/5 text-slate-500'}`}>{i + 1}</div>
                  {i < 3 && <div className={`w-6 h-0.5 ${['amount', 'pin', 'code', 'confirm'].indexOf(transferStep) > i ? 'bg-[#8B5CF6]' : 'bg-white/10'}`} />}
                </div>
              ))}
            </div>

            {/* Step 1: Amount & Method */}
            {transferStep === 'amount' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/5 text-center">
                  <p className="text-xs text-slate-400 mb-1">Available Balance</p>
                  <p className="text-2xl font-bold text-[#00CC99]">${transferBalance.available_balance.toFixed(2)}</p>
                </div>
                <div><Label className="text-slate-300 text-xs">Transfer Amount ($)</Label><Input type="number" step="0.01" min="0.01" value={transferForm.amount} onChange={e => setTransferForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl text-lg" data-testid="transfer-amount" /></div>
                <div>
                  <Label className="text-slate-300 text-xs mb-2 block">Transfer To</Label>
                  <div className="space-y-2">
                    {bankingInfo.map(info => (
                      <button key={info.method} onClick={() => setTransferForm(p => ({...p, method: info.method}))} className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${transferForm.method === info.method ? `border-opacity-40 shadow-[0_0_12px_rgba(139,92,246,0.15)]` : 'bg-white/5 border-white/10 hover:border-white/20'}`} style={transferForm.method === info.method ? { borderColor: methodColors[info.method], background: `${methodColors[info.method]}10` } : {}} data-testid={`transfer-method-${info.method}`}>
                        <div className="p-1.5 rounded-lg" style={{ background: `${methodColors[info.method]}20` }}>
                          {info.method === 'etransfer' ? <Mail size={14} style={{ color: methodColors[info.method] }} /> : <Building2 size={14} style={{ color: methodColors[info.method] }} />}
                        </div>
                        <div><p className="text-sm text-white">{methodLabels[info.method]}</p><p className="text-[10px] text-slate-500 font-mono">{info.method === 'etransfer' ? info.masked_data?.etransfer_email : `${info.masked_data?.institution} · ${info.masked_data?.account_number}`}</p></div>
                      </button>
                    ))}
                    {/* Stripe/PayPal withdrawal options */}
                    <button onClick={() => setTransferForm(p => ({...p, method: 'stripe'}))} className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${transferForm.method === 'stripe' ? 'bg-[#635BFF]/10 border-[#635BFF]/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="transfer-method-stripe">
                      <div className="p-1.5 rounded-lg bg-[#635BFF]/20"><CreditCard size={14} className="text-[#635BFF]" /></div>
                      <div><p className="text-sm text-white">Stripe</p><p className="text-[10px] text-slate-500">Withdraw to Stripe account</p></div>
                    </button>
                    <button onClick={() => setTransferForm(p => ({...p, method: 'paypal'}))} className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${transferForm.method === 'paypal' ? 'bg-[#0070BA]/10 border-[#0070BA]/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="transfer-method-paypal">
                      <div className="p-1.5 rounded-lg bg-[#0070BA]/20"><CreditCard size={14} className="text-[#0070BA]" /></div>
                      <div><p className="text-sm text-white">PayPal</p><p className="text-[10px] text-slate-500">Withdraw to PayPal account</p></div>
                    </button>
                  </div>
                </div>
                <button onClick={() => { if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) return toast.error('Enter a valid amount'); if (!transferForm.method) return toast.error('Select a transfer method'); setTransferStep('pin'); }} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-40 transition-all text-sm" data-testid="transfer-next-btn">Continue</button>
              </div>
            )}

            {/* Step 2: PIN Verification */}
            {transferStep === 'pin' && (
              <div className="space-y-4 text-center">
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20"><p className="text-sm text-amber-300">Enter your security PIN to continue</p></div>
                <Input type="password" maxLength={6} value={verifyPin} onChange={e => setVerifyPin(e.target.value.replace(/\D/g, ''))} placeholder="Enter PIN" className="bg-white/5 border-white/10 text-white rounded-xl tracking-[0.5em] text-center text-2xl py-4" data-testid="transfer-pin-input" />
                <button onClick={handleSendVerificationCode} disabled={!verifyPin || transferLoading} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_0_15px_rgba(245,158,11,0.3)] disabled:opacity-40 transition-all text-sm flex items-center justify-center gap-2" data-testid="transfer-verify-pin-btn">
                  {transferLoading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Verify PIN & Send Code
                </button>
              </div>
            )}

            {/* Step 3: Email Code */}
            {transferStep === 'code' && (
              <div className="space-y-4 text-center">
                <div className="p-3 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20"><p className="text-sm text-[#A78BFA]">A 6-digit code was sent to your email</p></div>
                {mockCode && <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20"><p className="text-xs text-yellow-300">Demo code (email mocked): <span className="font-mono font-bold text-lg">{mockCode}</span></p></div>}
                <Input maxLength={6} value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))} placeholder="Enter 6-digit code" className="bg-white/5 border-white/10 text-white rounded-xl tracking-[0.5em] text-center text-2xl py-4" data-testid="transfer-code-input" />
                <button onClick={handleVerifyCode} disabled={verifyCode.length !== 6 || transferLoading} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-40 transition-all text-sm flex items-center justify-center gap-2" data-testid="transfer-verify-code-btn">
                  {transferLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Verify Code
                </button>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {transferStep === 'confirm' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#00CC99]/5 border border-[#00CC99]/20 text-center"><CheckCircle size={24} className="text-[#00CC99] mx-auto mb-2" /><p className="text-sm text-[#00CC99] font-medium">Identity verified</p></div>
                <div className="p-4 rounded-xl bg-white/5 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-slate-400">Amount</span><span className="text-white font-bold text-lg">${parseFloat(transferForm.amount).toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-400">Method</span><span className="text-white">{methodLabels[transferForm.method]}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-400">Status</span><span className="text-amber-400">Will begin processing</span></div>
                </div>
                <button onClick={handleConfirmTransfer} disabled={transferLoading} className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-[#00CC99] to-emerald-600 shadow-[0_0_20px_rgba(0,204,153,0.3)] hover:shadow-[0_0_30px_rgba(0,204,153,0.5)] disabled:opacity-40 transition-all text-sm flex items-center justify-center gap-2" data-testid="transfer-confirm-btn">
                  {transferLoading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />} Confirm Transfer
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Pay Link Dialog */}
      <Dialog open={showCreatePayLink} onOpenChange={setShowCreatePayLink}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Link2 size={18} className="text-[#8B5CF6]" /> Create Pay Link</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="create-pay-link-form">
            {/* Image Upload */}
            <div>
              <Label className="text-[#94A3B8] text-xs">Item Image (optional)</Label>
              {payLinkForm.image_url ? (
                <div className="relative mt-1.5 aspect-video rounded-xl overflow-hidden border border-white/10 max-w-[200px]">
                  <img src={payLinkForm.image_url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPayLinkForm(p => ({ ...p, image_url: '' }))} className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center"><X size={12} className="text-white" /></button>
                </div>
              ) : (
                <label className="mt-1.5 flex items-center gap-2 p-3 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-[#8B5CF6]/30 transition-colors" data-testid="pay-link-image-upload">
                  {payLinkUploading ? <Loader2 size={16} className="animate-spin text-[#8B5CF6]" /> : <Upload size={16} className="text-[#64748B]" />}
                  <span className="text-xs text-[#64748B]">{payLinkUploading ? 'Uploading...' : 'Click to upload image'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePayLinkImageUpload} />
                </label>
              )}
            </div>

            <div>
              <Label className="text-[#94A3B8] text-xs">Item Name *</Label>
              <Input value={payLinkForm.item_name} onChange={e => setPayLinkForm(p => ({...p, item_name: e.target.value}))} placeholder="e.g., Custom Logo Design" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl" data-testid="pay-link-item-name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">Amount *</Label>
                <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span><Input type="number" step="0.01" value={payLinkForm.amount} onChange={e => setPayLinkForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" className="bg-white/5 border-white/10 text-white pl-7 rounded-xl" data-testid="pay-link-amount" /></div>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Item Type</Label>
                <select value={payLinkForm.item_type} onChange={e => setPayLinkForm(p => ({...p, item_type: e.target.value}))} className="mt-1 w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm" data-testid="pay-link-item-type">
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                  <option value="subscription">Subscription</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8] text-xs">Description (optional)</Label>
              <Textarea value={payLinkForm.description} onChange={e => setPayLinkForm(p => ({...p, description: e.target.value}))} placeholder="Brief description of what the customer is paying for..." rows={2} className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="pay-link-description" />
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowCreatePayLink(false)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#9CA3AF] hover:bg-white/5 transition-colors text-sm">Cancel</button>
              <button onClick={createPayLink} className="flex-1 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all text-sm" data-testid="save-pay-link-btn">Create Pay Link</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Current Plan + AI Upgrade card — sits at the top of the Overview tab
function PlanCard() {
  const [plan, setPlan] = useState({ tier: 'starter', monthly_price: 30 });
  const [aiCredits, setAiCredits] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/builder/ai/credits`, { headers: h })
      .then(r => setAiCredits(r.data))
      .catch(() => {});
    axios.get(`${API}/builder/plan-status`, { headers: h })
      .then(r => {
        const sp = r.data?.selected_plan;
        if (sp) setPlan({ tier: sp.id, monthly_price: sp.monthly_price });
      })
      .catch(() => {});
  }, []);

  const isPro = (aiCredits?.tier === 'pro') || (plan?.tier === 'pro');

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const r = await axios.post(`${API}/builder/ai/upgrade-tier`, { tier: 'pro' }, { headers: h });
      setAiCredits(c => ({ ...(c || {}), tier: 'pro', monthly_credits: r.data.monthly_credits, credits_used: 0, credits_remaining: r.data.monthly_credits }));
      toast.success('Welcome to Pro · 100 AI credits unlocked');
    } catch {
      toast.error('Upgrade failed');
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <GlassCard className="p-6" data-testid="fin-plan-card">
      <div className="flex items-start gap-5 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center shrink-0 shadow-[0_0_24px_rgba(139,92,246,0.35)]">
          <Shield size={26} className="text-white" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Current Plan</p>
          <h3 className="text-[22px] font-bold text-white font-syne tracking-tight">{isPro ? 'Pro · $75/month' : 'Starter · $30/month'}</h3>
          <p className="text-[12.5px] text-[#9CA3AF] mt-1.5 leading-relaxed">
            {isPro
              ? 'Includes 100 AI generations, priority support, unlimited section regenerations.'
              : 'Includes website builder, basic dashboards, and 3 AI generations/month.'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {!isPro && (
            <button
              onClick={upgrade}
              disabled={upgrading}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[12px] font-bold uppercase tracking-wider hover:shadow-[0_0_28px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50"
              data-testid="upgrade-pro-btn"
            >
              {upgrading ? 'Upgrading…' : 'Upgrade to Pro · $75/mo'}
            </button>
          )}
          {aiCredits && (
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold">
              {Math.max(0, aiCredits.credits_remaining)}/{aiCredits.monthly_credits} AI credits
            </p>
          )}
          {isPro && <span className="px-3 py-1 rounded-full bg-[#A78BFA]/15 text-[#A78BFA] text-[10px] font-bold uppercase tracking-wider">Active</span>}
        </div>
      </div>
      {!isPro && (
        <div className="mt-5 pt-5 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-3 gap-3 text-[11.5px] text-[#9CA3AF]">
          <div className="flex items-center gap-2"><CheckCircle size={12} className="text-[#A78BFA]" /> AI Website Generator (100/mo)</div>
          <div className="flex items-center gap-2"><CheckCircle size={12} className="text-[#A78BFA]" /> AI Section Generator</div>
          <div className="flex items-center gap-2"><CheckCircle size={12} className="text-[#A78BFA]" /> Priority support</div>
        </div>
      )}
    </GlassCard>
  );
}
